import { useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import { useCallback } from 'react';

import { CREDIT_CALCULATION_SERIALIZATION_KEY } from '@neo.tax/rd/constants';
import { useRdContext } from '@neo.tax/rd/context';
import { useGetDocuments } from '@neo.tax/rd/hooks';
import {
  FilingMethod,
  UPDATE_RD_TAX_CREDIT_OVERRIDES,
  UpdateRdTaxCreditOverridesMutation,
  UpdateRdTaxCreditOverridesMutationVariables,
  GET_CREDIT_ESTIMATE,
  GetCreditEstimateQuery,
  GetCreditEstimateQueryVariables,
  useAnalyticId,
} from '@neo.tax/shared';

const UPDATE_REDUCED_CREDIT_KEY = 'UPDATE_REDUCED_CREDIT_KEY';

// Section 280C reduction multipliers used for optimistic cache updates.
// ASC: reduced = base × 0.79. Regular: reduced = base × 0.158, non-reduced = base × 0.20.
const ASC_REDUCED_MULTIPLIER = 0.79;
const REGULAR_REDUCED_RATE = 0.158;
const REGULAR_NON_REDUCED_RATE = 0.2;

interface ReducedCreditForm {
  isReducedCredit: boolean | undefined;
  handleReducedCreditChange: (value: string) => void;
  analyticId: string;
}

export const useReducedCreditForm = (): ReducedCreditForm => {
  const analyticId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_REDUCED_CREDIT);
  const { fiscalYear } = useRdContext();
  const { documents } = useGetDocuments({ fiscalYearId: fiscalYear.id });

  const isReducedCredit = documents?.federalRdTaxCreditDocuments?.isReducedCredit;
  const filingMethod = documents?.federalRdTaxCreditDocuments?.filingMethod;

  const [updateRdTaxCreditOverrides] = useMutation<
    UpdateRdTaxCreditOverridesMutation,
    UpdateRdTaxCreditOverridesMutationVariables
  >(UPDATE_RD_TAX_CREDIT_OVERRIDES);

  const handleReducedCreditChange = useCallback(
    (value: string) => {
      const useReducedCredit = value === 'yes';
      updateRdTaxCreditOverrides({
        variables: {
          fiscalYearId: fiscalYear.id,
          rdTaxCreditOverrides: {
            useReducedCredit,
          },
        },
        optimisticResponse: {
          updateRdTaxCreditOverrides: {
            __typename: 'FederalRdTaxCreditDocuments',
            id: fiscalYear.id,
            filingMethod: filingMethod ?? FilingMethod.Asc,
            isReducedCredit: useReducedCredit,
          },
        },
        update: (cache) => {
          const existing = cache.readQuery<GetCreditEstimateQuery, GetCreditEstimateQueryVariables>({
            query: GET_CREDIT_ESTIMATE,
            variables: { fiscalYearId: fiscalYear.id },
          });

          if (existing?.getCreditEstimate.__typename !== 'Form6765Values') return;

          const currentValues = existing.getCreditEstimate;

          // Always apply the multiplier based on target state. Each update reads
          // the cache (which includes previous optimistic writes), so rapid
          // toggles compound correctly without relying on a render-time closure.
          const ascMultiplier = useReducedCredit ? ASC_REDUCED_MULTIPLIER : 1 / ASC_REDUCED_MULTIPLIER;
          const regularMultiplier = useReducedCredit
            ? REGULAR_REDUCED_RATE / REGULAR_NON_REDUCED_RATE
            : REGULAR_NON_REDUCED_RATE / REGULAR_REDUCED_RATE;

          cache.writeQuery<GetCreditEstimateQuery, GetCreditEstimateQueryVariables>({
            query: GET_CREDIT_ESTIMATE,
            variables: { fiscalYearId: fiscalYear.id },
            data: {
              getCreditEstimate: {
                ...currentValues,
                ascMethodCreditCents: Math.round(Number(currentValues.ascMethodCreditCents) * ascMultiplier),
                regularMethodCreditCents: Math.round(
                  Number(currentValues.regularMethodCreditCents) * regularMultiplier,
                ),
              },
            },
          });
        },
        context: {
          debounceKey: UPDATE_REDUCED_CREDIT_KEY,
          serializationKey: CREDIT_CALCULATION_SERIALIZATION_KEY,
        },
      });
    },
    [filingMethod, fiscalYear.id, updateRdTaxCreditOverrides],
  );

  return {
    isReducedCredit,
    handleReducedCreditChange,
    analyticId,
  };
};
